/**
 * Channel unread badges + the read cursor (docs/CHANNELS.md "Unread and the
 * read cursor", "The badge surfaces and `CountBadge`").
 *
 * Both go through the Hono backend (`/api/v1/channels`,
 * apps/api/src/routes/platform/channels.ts) — the count is `get_channel_unread()`
 * under the caller's own RLS, the cursor an upsert into `channel_reads`.
 *
 * Freshness is mount / window focus, not live: nothing subscribes the badge
 * to Realtime. A comment posted while you are elsewhere shows on the tab the
 * next time the page mounts or the window regains focus.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authedGet, authedPost } from "@/lib/api";
import { addBreadcrumb } from "@/lib/sentry";
import type {
  ChannelContextType,
  ChannelUnreadResponse,
  MarkChannelReadResponse,
} from "@releve/shared";

/**
 * Query key for one record's unread count. The last segment is whichever
 * identifier the surface has — `context_id` (accounts) or the org-scoped
 * `code` (the four resource detail routes, projects).
 */
export const channelUnreadKey = (
  orgId: string | undefined,
  contextType: ChannelContextType,
  idOrCode: string | undefined,
) => ["channel-unread", orgId, contextType, idOrCode] as const;

export interface ChannelUnreadQueryInput {
  contextType: ChannelContextType;
  /** The record's uuid — required for `account` (accounts have no code). */
  contextId?: string;
  /** The record's org-scoped `code`, what the detail routes carry in the URL. */
  contextCode?: string;
}

/**
 * Unread comments on one record's channel for the current user. `thread_id`
 * is null (and the count 0) when no thread exists yet or the code resolves to
 * nothing the caller can see.
 */
export function useChannelUnread(
  orgId: string | undefined,
  q: ChannelUnreadQueryInput,
  opts?: { enabled?: boolean },
) {
  const { contextType, contextId, contextCode } = q;
  return useQuery<ChannelUnreadResponse>({
    queryKey: channelUnreadKey(orgId, contextType, contextId ?? contextCode),
    queryFn: () => {
      const params = new URLSearchParams({
        context_type: contextType,
        ...(contextId ? { context_id: contextId } : { context_code: contextCode ?? "" }),
      });
      return authedGet<ChannelUnreadResponse>(`/api/v1/channels/orgs/${orgId}/unread?${params}`);
    },
    enabled: !!orgId && (!!contextId || !!contextCode) && (opts?.enabled ?? true),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Move the caller's read cursor on a thread to now. On success every cached
 * `channel-unread` entry for that thread (whichever key it was fetched under)
 * drops to 0 without a refetch.
 */
export function useMarkChannelRead(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      authedPost<MarkChannelReadResponse>(
        `/api/v1/channels/orgs/${orgId}/threads/${threadId}/read`,
        {},
      ),
    // Cancel any in-flight badge GET so a stale response cannot land after
    // the success patch below and put the count back.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["channel-unread", orgId] });
    },
    onSuccess: (_, threadId) => {
      queryClient.setQueriesData<ChannelUnreadResponse>(
        { queryKey: ["channel-unread", orgId] },
        (old) => (old && old.thread_id === threadId ? { ...old, unread_count: 0 } : old),
      );
    },
    // A read-cursor miss is not user-facing: the badge simply stays until the
    // next successful mark. No toast, no Sentry event — the next panel mount
    // retries. A breadcrumb only, so the miss shows up in the trail if
    // something else is reported later.
    onError: (error, threadId) =>
      addBreadcrumb(
        "channel mark-read failed",
        { threadId, message: error instanceof Error ? error.message : String(error) },
        "warning",
      ),
  });
}
